import { useState } from 'react';
import { RARITY_COLORS, formatStatMods, LEGENDARY_ABILITIES } from '../../data/items';
import { rollEnhancement, formatEnhancement, rarityAttrHint } from '../../data/enhancements';

import { POTION_COST, POTION_MAX } from '../../constants';

const ENHANCE_COST = 100;

/**
 * Crafting screen — two sections:
 *  1. HP Potion brewing: pays 100g to craft one potion (max 10 held).
 *  2. Item Enhancement: pays 100g to imbue any inventory item with random bonus stats.
 *
 * Enhancement level = playerLevel ± 5.
 * Attributes per rarity: common 1, uncommon 2, rare 2–3, epic 3–4, legendary 4.
 * Existing enhancement is overridden by the new one.
 */
export function EnhancementScreen({ onBack, inventory, onEnhance, spendGold, totalGold, playerLevel = 1, potionCount = 0, onCraftPotion }) {
  const [selectedIdx, setSelectedIdx]   = useState(null);
  const [flashResult,  setFlashResult]  = useState(null);   // { lines[] } when just enhanced

  // Only non-null, non-rune items can be enhanced
  const eligible = inventory
    .map((item, idx) => ({ item, idx }))
    .filter(({ item }) => item && item.slotType !== 'rune');

  const selectedEntry = selectedIdx !== null
    ? eligible.find(e => e.idx === selectedIdx) ?? null
    : null;
  const selectedItem = selectedEntry?.item ?? null;

  const canEnhance = !!selectedItem && totalGold >= ENHANCE_COST;

  const handleEnhance = () => {
    if (!canEnhance) return;
    const enhancement = rollEnhancement(playerLevel, selectedItem.rarity);
    onEnhance(selectedIdx, enhancement);
    spendGold(ENHANCE_COST);
    setFlashResult({ lines: formatEnhancement(enhancement), level: enhancement.level });
    // Clear flash after 2.5 s
    setTimeout(() => setFlashResult(null), 2500);
  };

  const handleSelect = (idx) => {
    setSelectedIdx(idx);
    setFlashResult(null);
  };

  const rarityColor = RARITY_COLORS[selectedItem?.rarity] ?? '#9e9e9e';

  const canCraftPotion = totalGold >= POTION_COST && potionCount < POTION_MAX;

  return (
    <div className="enhancement-screen">
      <button className="back-btn" onClick={onBack}>← Back</button>

      <h1 className="enhancement-title">⚗️ Crafting</h1>
      <div className="enhancement-subtitle">
        Brew potions and imbue items with powerful magical properties
      </div>

      <div className="enhancement-gold-badge">
        💰 <span className="enh-gold-amount">{totalGold}g</span> available
      </div>

      {/* ── HP Potion crafting ────────────────────────────────────────────── */}
      <div className="potion-craft-section">
        <div className="potion-craft-card">
          <span className="potion-craft-icon">🧪</span>
          <div className="potion-craft-info">
            <div className="potion-craft-name">HP Potion</div>
            <div className="potion-craft-desc">Restores 15 HP · 10 s cooldown · Key: R</div>
            <div className="potion-craft-stock">
              {potionCount}/{POTION_MAX} held
              {potionCount >= POTION_MAX && <span className="potion-craft-full"> — Full</span>}
            </div>
          </div>
          <button
            className={`potion-craft-btn${canCraftPotion ? '' : ' potion-craft-btn--disabled'}`}
            onClick={canCraftPotion ? onCraftPotion : undefined}
            disabled={!canCraftPotion}
          >
            {potionCount >= POTION_MAX
              ? 'At capacity'
              : totalGold < POTION_COST
                ? `Need ${POTION_COST}g`
                : `Brew for ${POTION_COST}💰`}
          </button>
        </div>
      </div>

      <div className="enhancement-layout">

        {/* ── Left column: inventory grid ── */}
        <div className="enhancement-inv">
          <div className="enhancement-inv-header">
            Select an item
          </div>
          {eligible.length === 0 ? (
            <div className="enh-empty-msg">No items in inventory</div>
          ) : (
            <div className="enhancement-inv-grid">
              {eligible.map(({ item, idx }) => {
                const col      = RARITY_COLORS[item.rarity] ?? '#9e9e9e';
                const selected = idx === selectedIdx;
                return (
                  <div
                    key={idx}
                    className={`enh-slot${selected ? ' enh-slot--selected' : ''}${item.enhancement ? ' enh-slot--has-enh' : ''}`}
                    style={{ '--rarity-color': col }}
                    onClick={() => handleSelect(idx)}
                    title={`${item.name}${item.enhancement ? ` [✦ Lv ${item.enhancement.level}]` : ''}`}
                  >
                    <span className="enh-slot-icon">{item.icon}</span>
                    {item.enhancement && (
                      <span className="enh-slot-badge">✦</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Right column: detail panel ── */}
        <div className="enhancement-panel">
          {!selectedItem ? (
            <div className="enh-panel-hint">
              <div className="enh-hint-icon">⚗️</div>
              <div>Select an item from your inventory to enhance it.</div>
              <div className="enh-hint-sub">Each enhancement costs <strong>{ENHANCE_COST}g</strong>.</div>
            </div>
          ) : (
            <div className="enh-detail">

              {/* Item header */}
              <div className="enh-item-header" style={{ '--rarity-color': rarityColor }}>
                <span className="enh-item-big-icon">{selectedItem.icon}</span>
                <div className="enh-item-info">
                  <div className="enh-item-name">{selectedItem.name}</div>
                  <div className="enh-item-meta" style={{ color: rarityColor }}>
                    {selectedItem.rarity.charAt(0).toUpperCase() + selectedItem.rarity.slice(1)}
                    {' · '}Level {selectedItem.levelReq}
                  </div>
                </div>
              </div>

              {/* Base stats */}
              <div className="enh-section">
                <div className="enh-section-title">Base Stats</div>
                {formatStatMods(selectedItem.statMods).map(l => (
                  <div key={l} className="enh-stat-line">▸ {l}</div>
                ))}
                {selectedItem.abilityId && (() => {
                  const ab = LEGENDARY_ABILITIES[selectedItem.abilityId];
                  return (
                    <div className="enh-stat-line enh-stat-line--ability">
                      ⚡ {ab?.name ?? selectedItem.abilityId}
                      {ab?.desc && <span className="enh-ability-desc"> — {ab.desc}</span>}
                    </div>
                  );
                })()}
              </div>

              {/* Current enhancement or placeholder */}
              {selectedItem.enhancement && !flashResult ? (
                <div className="enh-section enh-section--active">
                  <div className="enh-section-title enh-section-title--active">
                    ✦ Enhancement
                    <span className="enh-level-pill">Lv {selectedItem.enhancement.level}</span>
                  </div>
                  {formatEnhancement(selectedItem.enhancement).map(l => (
                    <div key={l} className="enh-stat-line enh-stat-line--enh">✦ {l}</div>
                  ))}
                </div>
              ) : !flashResult && (
                <div className="enh-no-enh">No enhancement — slot is empty</div>
              )}

              {/* Flash result after enhancing */}
              {flashResult && (
                <div className="enh-section enh-section--result">
                  <div className="enh-result-header">✨ New Enhancement!</div>
                  <div className="enh-result-level">Level {flashResult.level}</div>
                  {flashResult.lines.map(l => (
                    <div key={l} className="enh-stat-line enh-stat-line--result">✦ {l}</div>
                  ))}
                </div>
              )}

              {/* What to expect */}
              <div className="enh-forecast">
                <span className="enh-forecast-row">
                  📊 Level range:&nbsp;<strong>{Math.max(1, playerLevel - 5)}–{playerLevel + 5}</strong>
                </span>
                <span className="enh-forecast-row">
                  🎲 Attributes:&nbsp;<strong>{rarityAttrHint(selectedItem.rarity)}</strong>
                </span>
                {selectedItem.enhancement && (
                  <span className="enh-forecast-row enh-forecast-row--warn">
                    ⚠️ Existing enhancement will be replaced
                  </span>
                )}
              </div>

              {/* Enhance button */}
              <button
                className={`enh-btn${canEnhance ? '' : ' enh-btn--disabled'}`}
                onClick={handleEnhance}
                disabled={!canEnhance}
              >
                {totalGold < ENHANCE_COST
                  ? `💰 Need ${ENHANCE_COST}g (have ${totalGold}g)`
                  : `⚗️ Enhance for ${ENHANCE_COST}💰`}
              </button>

            </div>
          )}
        </div>

      </div>
    </div>
  );
}
